import { useState, useEffect } from 'react';
import { Box, Typography, Checkbox, FormControlLabel } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Modal } from '@components/Modal';

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: (includeAcknowledgment: boolean) => void;
  isSubmitting: boolean;
  hasSignature: boolean;
  signaturePreviewUrl?: string | null;
  printedName?: string;
};

export const SubmitConfirmationModal = ({
  open,
  onClose,
  onConfirm,
  isSubmitting,
  hasSignature,
  signaturePreviewUrl,
  printedName
}: Props) => {
  const { t } = useTranslation('customerReview');
  const [includeAcknowledgment, setIncludeAcknowledgment] =
    useState(hasSignature);

  useEffect(() => {
    if (open) {
      setIncludeAcknowledgment(hasSignature);
    }
  }, [open, hasSignature]);

  const handleConfirm = () => {
    onConfirm(includeAcknowledgment);
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIncludeAcknowledgment(event.target.checked);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Modal open={open} onClose={handleClose} width="500px">
      <Modal.Header
        title={t('acknowledgment.confirmModal.title')}
        onClose={isSubmitting ? undefined : handleClose}
      />

      <Modal.Content>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('acknowledgment.confirmModal.description')}
        </Typography>

        <Box
          sx={{
            p: 2,
            borderRadius: 1,
            backgroundColor: 'action.hover',
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={includeAcknowledgment}
                onChange={handleCheckboxChange}
                disabled={!hasSignature || isSubmitting}
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight={500}>
                  {t('acknowledgment.confirmModal.includeAcknowledgment')}
                </Typography>
                {!hasSignature && (
                  <Typography variant="caption" color="text.secondary">
                    {t('acknowledgment.confirmModal.noSignature')}
                  </Typography>
                )}
              </Box>
            }
          />

          {hasSignature && signaturePreviewUrl && includeAcknowledgment && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: 'background.paper',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              {printedName && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 1, display: 'block' }}
                >
                  {printedName}
                </Typography>
              )}
              <Box
                component="img"
                src={signaturePreviewUrl}
                alt="Signature preview"
                sx={{
                  maxWidth: '100%',
                  maxHeight: 80,
                  objectFit: 'contain'
                }}
              />
            </Box>
          )}
        </Box>

        {!includeAcknowledgment && (
          <Typography
            variant="body2"
            color="warning.main"
            sx={{ mt: 2, fontStyle: 'italic' }}
          >
            {t('acknowledgment.confirmModal.noAcknowledgmentWarning')}
          </Typography>
        )}
      </Modal.Content>

      <Modal.Footer
        secondaryButton={{
          text: t('acknowledgment.confirmModal.cancel'),
          onClick: handleClose,
          disabled: isSubmitting
        }}
        primaryButton={{
          text: t('acknowledgment.confirmModal.confirm'),
          onClick: handleConfirm,
          loading: isSubmitting,
          disabled: isSubmitting
        }}
      />
    </Modal>
  );
};
